import React from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";

// 1. 定义可选的类型：根据业务分为 Inflow(买盘) 和 Outflow(卖盘)
export type FlowType = "inflow" | "outflow" | "buy" | "sell";

interface FlowBarProps {
  label: string; // 例如 "Buy Pressure" 或 "Inflow"
  percentage: number; // 0 到 100 的数字
  type: FlowType; // 类型控制颜色
}

export const FlowBarComponent = ({ label, percentage, type }: FlowBarProps) => {
  // 约束百分比
  const validPercentage = Math.max(0, Math.min(100, percentage));
  const data = [{ name: label, value: validPercentage }];

  // 💡 视觉色彩规范：
  // Inflow / Buy Pressure ➡️ 科技绿 (Emerald)
  // Outflow / Sell Pressure ➡️ 警示红 (Rose)
  const colors = {
    inflow: {
      bar: "#8b5cf6",
      bg: "rgba(139,92,246,0.16)",
    },
    outflow: {
      bar: "#7c83fd",
      bg: "rgba(124,131,253,0.14)",
    },
    buy: {
      bar: "#c4b5fd",
      bg: "rgba(139,92,246,0.16)",
    },
    sell: {
      bar: "#a78bfa",
      bg: "rgba(124,131,253,0.14)",
    },
  } as const;

  const currentTheme = colors[type];

  return (
    <div
      className="flex-1 min-w-[150px]"
      style={{
        width: "100%",
        background: "transparent" /* 暗色极客底色 */,
        padding: "14px 16px",
        borderRadius: "8px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {/* 🔝 上层：文字区（标签与数据完美贴在两端） */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{ color: currentTheme.bar, fontSize: "14px", fontWeight: 500 }}
        >
          {label.toUpperCase()}
        </span>
        <span
          style={{
            color: currentTheme.bar /* 数字与进度条颜色同步 */,
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          {validPercentage}%
        </span>
      </div>

      {/* 底部：进度条轨道 */}
      <div
        style={{
          width: "100%",
          height: "8px" /* 条形图的厚度 */,
          background: currentTheme.bg /* 动态有色透明轨道 */,
          borderRadius: "4px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <XAxis type="number" domain={[0, 100]} hide />
            <YAxis type="category" dataKey="name" hide />
            <Bar
              dataKey="value"
              fill={currentTheme.bar}
              isAnimationActive={true}
              radius={[0, 4, 4, 0]} /* 只给条形图的右侧边缘加圆角 */
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
